import { api } from './client';

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

// ---------- Organizations ----------
export type TaxSystem =
  | 'GENERAL' | 'SIMPLIFIED_3' | 'SIMPLIFIED_4'
  // Block 8.1 (2026-05-26): ФОП values
  | 'FOP_1' | 'FOP_2' | 'FOP_3_3' | 'FOP_3_5'
  // Block 11 (2026-05-27): ФОП загальна система
  | 'FOP_GENERAL'
  | 'NONE';   // ч.1401: фізособа «лише ЕДО» — обліку немає

// Block A (2026-05-31): entity_type разделяет ТОВ vs ФОП.
export type EntityType = 'LEGAL_ENTITY' | 'FOP' | 'INDIVIDUAL';   // ч.1401: фізособа — лише ЕДО

/** ч.1383: рядок податкового статусу організації «з дати» (як облікова політика 1С). */
export interface OrgTaxStatus {
  id?: string | null;
  valid_from: string;
  tax_system: TaxSystem;
  is_vat_payer: boolean;
  vat_number: string | null;
  /** 'card' — введено в сервісі, '1c' — з облікової політики 1С. */
  source?: 'card' | '1c';
}

export interface Organization {
  id: string;
  tenant_id: string;
  // Auto-code (2026-06-02): може бути null при створенні
  // (backend генерує "ORG-N"); після збереження завжди заповнено.
  code: string | null;
  name: string;
  full_name: string;
  entity_type: EntityType;
  edrpou: string | null;
  inn: string | null;
  is_vat_payer: boolean;
  vat_number: string | null;
  /** ч.1383: податковий статус з дати (за зростанням); поля вище — статус на сьогодні. */
  tax_history?: OrgTaxStatus[];
  /** ч.1374: умовчання ознак ПДВ нових документів (1С УчитыватьНДС / СуммаВключаетНДС / АвторасчетНДС). */
  vat_considered_default?: boolean;
  prices_include_vat_default?: boolean;
  vat_auto_default?: boolean;
  // ч.1058/ч.1110: підтвердження організації — підписання договору з сервісом
  // ключем КЕП з ЄДРПОУ/РНОКПП організації (кнопка «підтвердити» у списку).
  // null → закриті відправка з Журналу ЕДО, імпорт з 1С, передача/видалення без ключа.
  kep_confirmed_at?: string | null;
  kep_confirmed_code?: string | null;
  // ч.1110: версія підписаного договору і запис Журналу ЕДО з ним.
  agreement_version?: number | null;
  agreement_signed_at?: string | null;
  agreement_entry_id?: string | null;
  // Block A: contact + address fields (були у БД, тепер у API/UI)
  legal_address: string | null;
  actual_address: string | null;
  phone: string | null;
  email: string | null;
  director_name: string | null;
  chief_accountant: string | null;
  // Реквізити звітності ДПС (0337): РНОКПП підписантів + коди ДПІ.
  director_inn?: string | null;
  chief_accountant_inn?: string | null;
  /** ч.1179: головбух потрапляє у підписи регламентованих звітів лише з цією галочкою. */
  chief_accountant_signs_reports?: boolean;
  sti_region?: number | null;
  sti_district?: number | null;
  sti_name?: string | null;
  sti_code?: string | null;
  cost_method: 'FIFO' | 'WAVG';
  production_costing_mode: 'SIMPLE' | '1C_WIP';
  tax_system: TaxSystem;
  // TD-B-P5.2.3 (2026-06-01): MATERNITY calc period config (12 vs 24 міс).
  maternity_calc_period_months: 12 | 24;
  fx_revaluation_period?: 'MONTHLY' | 'QUARTERLY';
  base_currency_code: string;
  business_template_id: string | null;
  use_double_entry: boolean;
  use_chart_of_accounts: boolean;
  use_recipes: boolean;
  is_diia_city_resident: boolean;
  /** ч.1208: норматив працевлаштування осіб з інвалідністю 2 % (заклади охорони здоровʼя / реабілітації). */
  disability_norm_2pct?: boolean;
  // 0452: зберігати переглянуті документи Кабінету ДПС вкладеннями org.
  // Опційне: старі тест-фікстури/wizard не зобов'язані його передавати.
  dps_store_docs?: boolean;
  use_retail: boolean;
  use_directions: boolean;
  use_returnable_packaging: boolean;
  /** ч.1376: ФОП-єдинник веде облік лише за Журналом доходів і витрат (з дати income_journal_since). */
  income_journal_only?: boolean;
  income_journal_since?: string | null;
  /** ч.1386: форми фінзвітності (MICRO 1-мс/2-мс, SMALL 1-м/2-м, FULL Ф1–Ф4) і період декларації з прибутку. */
  fin_report_form?: 'MICRO' | 'SMALL' | 'FULL';
  profit_tax_period?: 'AUTO' | 'QUARTER' | 'YEAR';
  // Перерахування податків ЗП: реквізити отримувача (ПДФО/ВЗ/ЄСВ).
  tax_pdfo_recipient_name: string | null;
  tax_pdfo_recipient_edrpou: string | null;
  tax_pdfo_recipient_iban: string | null;
  tax_wz_recipient_name: string | null;
  tax_wz_recipient_edrpou: string | null;
  tax_wz_recipient_iban: string | null;
  tax_esv_recipient_name: string | null;
  tax_esv_recipient_edrpou: string | null;
  tax_esv_recipient_iban: string | null;
  // ч.745: додаткові податкові рахунки (єдиний податок, ВЗ до ЄП, …) —
  // рядки ТЧ картки; опційне для старих фікстур.
  tax_accounts_extra?: OrgTaxAccount[];
  // ч.806: місця діяльності (відокремлені підрозділи) — лише юрособи.
  activity_places?: OrgActivityPlace[];
  // ч.767: шаблони призначення для ПДФО/ВЗ/ЄСВ {pdfo|wz|esv: template}.
  tax_purpose_templates?: Record<string, string>;
  // Block 8.1: ФОП registration data
  registration_date: string | null;
  // Block A: KVED split на основний + додаткові
  kved_main: string | null;
  kved_extra: string[];
  // ORG-EDITOR.1 (2026-06-04): ОПФГ + КОАТУУ для регл.звітності.
  opfg_code: string | null;
  opfg_name: string | null;
  // ч.746: назва без оргформи + скорочення (ТОВ/ПП/ФОП); name/full_name
  // компілює бекенд. Опційні для старих фікстур.
  own_name?: string | null;
  opf_short?: string | null;
  koatuu_code: string | null;
  // Block 8.1: computed fields
  single_tax_group: number | null;
  is_fop: boolean;
  // Phase-3.7 (2026-06-01): з якого 1С/BAS org мігрувалася.
  // NULL = немає міграції. Використовується UI <ArchiveBanner>.
  migration_source_config:
    | 'utp'
    | 'generic'
    | '1c_bp_ua_2x'
    | 'bas_bukh'
    | 'ut_ua'
    | null;
  deletion_mark: boolean;
  created_at: string;
  updated_at: string;
}

/** ч.806: місце діяльності (відокремлений підрозділ) юрособи: ПДФО працівників
 * місця — до місцевого бюджету за КАТОТТГ місця; окремий Податковий розрахунок
 * (лише 4ДФ), рядок 033 = name (тип обʼєкта з 20-ОПП), 032 = katottg. */
export interface OrgActivityPlace {
  id?: string | null;
  organization_id?: string;
  name: string;
  object_type_code: string | null;
  katottg: string | null;
  address: string | null;
  pdfo_recipient_name: string | null;
  pdfo_recipient_edrpou: string | null;
  pdfo_recipient_iban: string | null;
  pdfo_purpose_template: string | null;
}

export interface OrgTaxAccount {
  name: string;
  recipient_name: string | null;
  recipient_edrpou: string | null;
  recipient_iban: string | null;
  // ч.767: шаблон призначення платежу (змінні {edrpou} {tax} {period} …).
  purpose_template?: string | null;
}

export type OrganizationCreate = Omit<
  Organization,
  'id' | 'tenant_id' | 'deletion_mark'
  | 'created_at' | 'updated_at'
  | 'single_tax_group' | 'is_fop'
  | 'migration_source_config'
  | 'is_vat_payer'
> & {
  /** ч.1381: у нової організації не заданий (null), поки бухгалтер не обере «так / ні». */
  is_vat_payer: boolean | null;
};

/** ч.1110: сесія підписання договору з сервісом (текст, який треба підписати як є). */
export interface AgreementSigning {
  signing_id: string;
  version: number;
  title: string;
  document_text: string;
  expires_in_seconds: number;
}

export const orgs = {
  /** ч.1407: `kind` — за замовчуванням `auto` (у режимі «ЕДО» — лише картки учасника, інакше — лише
   *  організації з обліком); `business` / `edo_card` / `all` — явно. Картка ЕДО — не організація. */
  list: (params?: { q?: string; limit?: number; offset?: number; kind?: 'auto' | 'business' | 'edo_card' | 'all' }) =>
    api.get<Page<Organization>>('/organizations', { params }).then((r) => r.data),
  // ч.1110: підтвердження організації підписанням договору ключем КЕП.
  agreementSigning: (id: string) =>
    api.post<AgreementSigning>(`/organizations/${id}/agreement-signing`).then((r) => r.data),
  confirm: (id: string, payload: { signing_id: string; signature_b64: string; timestamped: boolean }) =>
    api.post<Organization>(`/organizations/${id}/confirm`, payload, { headers: { 'X-Silent-422': '1' } })
      .then((r) => r.data),
  create: (payload: OrganizationCreate) =>
    api.post<Organization>('/organizations', payload).then((r) => r.data),
  update: (id: string, payload: Partial<OrganizationCreate>) =>
    api.patch<Organization>(`/organizations/${id}`, payload).then((r) => r.data),
};

// ---------- Contractors ----------
export type ContractorType = 'LEGAL' | 'INDIVIDUAL_FOP' | 'INDIVIDUAL' | 'SEPARATE_DIVISION';

// Норма #20 v2 (2026-06-02): tax_system синхронізовано з backend.
export type ContractorTaxSystem =
  | 'GENERAL' | 'SIMPLIFIED_3' | 'SIMPLIFIED_4'
  | 'FOP_GENERAL' | 'FOP_1' | 'FOP_2' | 'FOP_3_3' | 'FOP_3_5';

export interface Contractor {
  id: string;
  code: string | null;
  name: string;
  full_name: string | null;
  contractor_type: ContractorType;
  edrpou: string | null;
  inn: string | null;
  is_vat_payer: boolean;
  vat_number: string | null;
  tax_system: ContractorTaxSystem | null;
  legal_address: string | null;
  actual_address: string | null;
  phone: string | null;
  email: string | null;
  is_buyer: boolean;
  is_supplier: boolean;
  is_other: boolean;
  // TD-NONRES + TD-GROUPS B (2026-06-06): нерезидент-роль + група контрагента.
  is_non_resident: boolean;
  group_id: string | null;
  // ч.747: оргформа (ОПФГ або FIZ «Фізична особа») + власна назва +
  // «Скорочено» (коротка назва як є) + регномер нерезидента. name/full_name
  // компілює бекенд. Опційні для старих фікстур.
  opfg_code?: string | null;
  opfg_name?: string | null;
  own_name?: string | null;
  opf_short?: string | null;
  reg_number?: string | null;
  // MIGRATION-EXT.2 (2026-06-05): Вид діяльності за КВЕД.
  kved_code: string | null;
  deletion_mark: boolean;
  // Migration Quarantine (2026-06-06): реквізити з 1С не за стандартом →
  // запис у карантині, заблокована для нових документів до виправлення.
  needs_review: boolean;
  /** ч.1094: картку обʼєднано з іншою (дубль) — id основної. */
  merged_into_id?: string | null;
  // Значок «І» імпорту з 1С/BAS.
  external_source?: string | null;
  created_at: string;
  updated_at: string;
}

// MIGRATION-EXT.1 (2026-06-05): пов'язані сутності контрагента.
export interface ContractorContract {
  id: string;
  contractor_id: string;
  organization_id: string;
  code: string;
  name: string;
  contract_date: string;
  contract_type: string;
  currency_id: string;
  settlement_type: string;
  payment_term_days: number | null;
  deletion_mark: boolean;
}

export interface ContractorBankAccount {
  id: string;
  owner_id: string;  // contractor id
  iban: string;
  currency_id: string;
  account_name: string | null;
  is_main: boolean;
  deletion_mark: boolean;
}

export interface ContractorDeliveryAddress {
  id: string;
  contractor_id: string;
  name: string | null;
  address: string;
  is_main: boolean;
}

export interface ContractorContact {
  id: string;
  contractor_id: string;
  name: string;
  position: string | null;
  phone: string | null;
  email: string | null;
  birthday: string | null;  // ISO date
  is_main: boolean;
}

export type ContractorCreate = Omit<
  Contractor,
  'id' | 'deletion_mark' | 'needs_review' | 'created_at' | 'updated_at'
>;

export type ContractorRoleFilter = 'buyer' | 'supplier' | 'non_resident' | 'other';

export const contractors = {
  list: (params?: {
    q?: string; group_id?: string; role?: ContractorRoleFilter;
    limit?: number; offset?: number; sort_by?: string; sort_dir?: 'asc' | 'desc';
  }) =>
    api.get<Page<Contractor>>('/contractors', { params }).then((r) => r.data),
  /** ч.1182: УВЕСЬ довідник (для мап id → назва у списках/редакторах). Сторінками по
   *  2000 до кінця: раніше 76 сторінок тягнули один ключ `contractors-all` з різними
   *  limit (500/1000/2000) — у акаунті з 752 контрагентами назви платників у списку
   *  банку то були, то зникали залежно від того, яка сторінка востаннє заповнила кеш. */
  listAll: async (): Promise<Page<Contractor>> => {
    const limit = 2000;
    const items: Contractor[] = [];
    let offset = 0;
    let total = 0;
    for (;;) {
      const page = await api.get<Page<Contractor>>('/contractors', { params: { limit, offset } })
        .then((r) => r.data);
      items.push(...page.items);
      total = page.total;
      offset += page.items.length;
      if (page.items.length < limit || offset >= total) break;
    }
    return { items, total: Math.max(total, items.length), limit, offset: 0 } as Page<Contractor>;
  },
  get: (id: string) =>
    api.get<Contractor>(`/contractors/${id}`).then((r) => r.data),
  create: (payload: ContractorCreate) =>
    api.post<Contractor>('/contractors', payload).then((r) => r.data),
  update: (id: string, payload: Partial<ContractorCreate>) =>
    api.patch<Contractor>(`/contractors/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/contractors/${id}`).then(() => undefined),
  /** ч.1095: чинні контрагенти з таким кодом (ЄДРПОУ/РНОКПП) — підказка проти дубля. */
  byCode: (code: string, excludeId?: string | null) =>
    api.get<ContractorCodeMatch[]>('/contractors/by-code',
      { params: { code, exclude_id: excludeId || undefined }, headers: { 'X-Silent-Toast': '1' } })
      .then((r) => r.data),
  /** ч.1237: чинні контрагенти, схожі на нового, — за кодом, рахунком або назвою. */
  similar: (p: { code?: string | null; iban?: string | null; name?: string | null; excludeId?: string | null }) =>
    api.get<ContractorSimilarMatch[]>('/contractors/similar', {
      params: { code: p.code || undefined, iban: p.iban || undefined, name: p.name || undefined,
        exclude_id: p.excludeId || undefined },
      headers: { 'X-Silent-Toast': '1' },
    }).then((r) => r.data),
  /** ч.1094: групи можливих дублів (однаковий код / назва). */
  duplicates: () => api.get<DuplicatesResponse>('/contractors/duplicates').then((r) => r.data),
  /** Обʼєднати дублі в основну картку (незворотно; власник/адміністратор). */
  merge: (main_id: string, duplicate_ids: string[]) =>
    api.post<{ merged: number; moved: Record<string, number> }>(
      '/contractors/merge', { main_id, duplicate_ids }, { headers: { 'X-Silent-Toast': '1' } })
      .then((r) => r.data),
  mergeObvious: () =>
    api.post<{ merged: number; groups: number; errors: string[] }>(
      '/contractors/merge-obvious', null, { headers: { 'X-Silent-Toast': '1' } }).then((r) => r.data),
};

export interface ContractorCodeMatch {
  id: string;
  name: string;
  full_name: string | null;
  code: string;
  contractor_type: string | null;
}

/** ч.1237: схожий контрагент і чому він схожий. */
export interface ContractorSimilarMatch {
  /** ч.1312: null — контрагент є, але закріплений за іншим користувачем (роль звужує
   *  контрагентів «лише свої»): ні назви, ні картки. */
  id: string | null;
  name: string;
  full_name: string | null;
  code: string | null;
  contractor_type: string | null;
  reasons: ('code' | 'iban' | 'name')[];
  hidden?: boolean;
}

export interface DuplicateMember {
  id: string;
  name: string;
  full_name: string | null;
  code: string | null;
  contractor_type: string | null;
  external_source: string | null;
  created_at: string;
  docs_count: number;
  contracts_count: number;
  organizations: string[];
  /** Номер «очевидного» кластера в групі; null — на перевірку. */
  cluster: number | null;
}

export interface DuplicateGroup {
  kind: 'code' | 'name';
  key: string;
  obvious: { main_id: string; duplicate_ids: string[] }[];
  members: DuplicateMember[];
}

export interface DuplicatesResponse {
  groups: DuplicateGroup[];
  obvious_merges: number;
  can_merge: boolean;
}

// ---------- CRUD пов'язаних сутностей контрагента (картка, вкладки) ----------
export type VatMethod = 'FIRST_EVENT' | 'ON_PAYMENT';
export interface ContractRow {
  comment?: string | null;
  id: string;
  contractor_id: string;
  organization_id: string;
  code: string;
  name: string;
  contract_type: string;
  currency_id: string;
  civil_kind_id: string | null;
  number: string | null;
  contract_date: string | null;
  valid_until: string | null;
  is_foreign_economic: boolean;
  vat_method: VatMethod;
  tare_vat_method: VatMethod;
  project_id: string | null;
  cash_flow_item_id: string | null;
  // 0372: повна модель картки договору (вкладки 1С).
  debt_control_amount?: boolean;
  debt_limit?: string | number | null;
  prepayment_percent?: string | number | null;
  debt_control_days?: boolean;
  payment_term_days?: number | null;
  price_type_id?: string | null;
  main_cost_item_id?: string | null;
  vat_rate_id?: string | null;
  discount_percent?: string | number | null;
  settlement_type?: string;
  print_deal_info?: boolean;
  print_name?: string | null;
  settlement_form?: string;
  is_main?: boolean;
}
// Рядок зведеного реєстру договорів (Довідники) — з ім'ям контрагента.
export interface ContractRegistryItem {
  id: string;
  contractor_id: string;
  contractor_name: string;
  organization_id: string;
  organization_name: string | null;
  code: string;
  name: string;
  number: string | null;
  contract_date: string | null;
  valid_until: string | null;
  currency_id: string;
  contract_type: string;
  civil_kind_id: string | null;
  is_foreign_economic: boolean;
  vat_method: VatMethod;
  tare_vat_method: VatMethod;
  project_id: string | null;
  // Значок «І» імпорту з 1С/BAS.
  external_source?: string | null;
}
export interface ContractCreate {
  comment?: string | null;
  contractor_id: string;
  organization_id: string;
  name: string;
  currency_id: string;
  contract_type?: string;
  civil_kind_id?: string | null;
  number?: string | null;
  contract_date?: string | null;
  valid_until?: string | null;
  is_foreign_economic?: boolean;
  vat_method?: VatMethod;
  tare_vat_method?: VatMethod;
  project_id?: string | null;
  cash_flow_item_id?: string | null;
  // 0372: повна модель картки договору (вкладки 1С).
  debt_control_amount?: boolean;
  debt_limit?: string | number | null;
  prepayment_percent?: string | number | null;
  debt_control_days?: boolean;
  payment_term_days?: number | null;
  price_type_id?: string | null;
  main_cost_item_id?: string | null;
  vat_rate_id?: string | null;
  discount_percent?: string | number | null;
  settlement_type?: string;
  print_deal_info?: boolean;
  print_name?: string | null;
  settlement_form?: string;
  is_main?: boolean;
}
// Read-only панель «Рахунки обліку» договору (обчислюється на льоту).
export interface ContractAccountLine {
  purpose: string;
  code: string;
  name: string | null;
}
export interface ContractAccountsPanel {
  contract_id: string;
  is_non_resident: boolean;
  is_foreign_currency: boolean;
  currency_code: string;
  lines: ContractAccountLine[];
}
export interface ContractorBankRow {
  id: string;
  owner_id: string;
  iban: string;
  currency_id: string;
  account_name: string | null;
  is_main: boolean;
}

export const contractorNested = {
  // Договори
  // Зведений реєстр (Довідники) — серверна пагінація + пошук + сорт + ім'я контрагента.
  listRegistry: (params: {
    q?: string; organization_id?: string; limit: number; offset: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
  }) =>
    api.get<Page<ContractRegistryItem>>('/refs/contracts-registry', { params }).then((r) => r.data),
  // Список договорів контрагента (для картки).
  listForContractor: (contractorId: string) =>
    api.get<ContractRow[]>('/refs/contracts', { params: { contractor_id: contractorId } }).then((r) => r.data),
  createContract: (p: ContractCreate) =>
    api.post<ContractRow>('/refs/contracts', p).then((r) => r.data),
  updateContract: (id: string, p: Partial<Omit<ContractCreate, 'contractor_id' | 'organization_id'>>) =>
    api.patch<ContractRow>(`/refs/contracts/${id}`, p).then((r) => r.data),
  removeContract: (id: string) =>
    api.delete(`/refs/contracts/${id}`).then(() => undefined),
  // Read-only панель рахунків обліку (обчислюється на льоту з резидентності+валюти)
  getContractAccounts: (id: string) =>
    api.get<ContractAccountsPanel>(`/refs/contracts/${id}/accounts`).then((r) => r.data),
  // Адреси постачання (0385): вкладка картки + попап «Адреса призначення» ТТН
  listDeliveryAddresses: (contractorId: string) =>
    api.get<ContractorDeliveryAddress[]>('/refs/contractor-delivery-addresses',
      { params: { contractor_id: contractorId } }).then((r) => r.data),
  createDeliveryAddress: (p: Omit<ContractorDeliveryAddress, 'id'>) =>
    api.post<ContractorDeliveryAddress>('/refs/contractor-delivery-addresses', p).then((r) => r.data),
  removeDeliveryAddress: (id: string) =>
    api.delete(`/refs/contractor-delivery-addresses/${id}`).then(() => undefined),
  // Контактні особи
  createContact: (p: Omit<ContractorContact, 'id'>) =>
    api.post<ContractorContact>('/refs/contractor-contacts', p).then((r) => r.data),
  updateContact: (id: string, p: Partial<Omit<ContractorContact, 'id' | 'contractor_id'>>) =>
    api.patch<ContractorContact>(`/refs/contractor-contacts/${id}`, p).then((r) => r.data),
  removeContact: (id: string) =>
    api.delete(`/refs/contractor-contacts/${id}`).then(() => undefined),
  // Банк-рахунки контрагента
  createBank: (p: { contractor_id: string; iban: string; currency_id: string; account_name?: string | null; is_main?: boolean }) =>
    api.post<ContractorBankRow>('/refs/contractor-bank-accounts', p).then((r) => r.data),
  updateBank: (id: string, p: { iban?: string; currency_id?: string; account_name?: string | null; is_main?: boolean }) =>
    api.patch<ContractorBankRow>(`/refs/contractor-bank-accounts/${id}`, p).then((r) => r.data),
  removeBank: (id: string) =>
    api.delete(`/refs/contractor-bank-accounts/${id}`).then(() => undefined),
};

// ---------- Nomenclature ----------
export interface Nomenclature {
  id: string;
  code: string | null;
  article: string | null;
  name: string;
  full_name: string | null;
  is_service: boolean;
  use_series?: boolean;
  /** Облік за характеристиками (варіантами товару). */
  use_characteristics?: boolean;
  is_excise: boolean;
  is_group: boolean;
  ukt_zed_code: string | null;
  country_origin_code: string | null;
  /** Штрих-код для POS-сканування (Порція Роздріб.6). */
  barcode: string | null;
  /** Базова одиниця виміру (2026-06-02). */
  base_uom_id: string | null;
  /** Ставка ПДВ за замовчуванням (бух-реквізит 1С УТП). */
  vat_rate_id: string | null;
  /** Стаття затрат за замовчуванням → ref.cost_items (alembic 0240). */
  default_cost_item_id: string | null;
  /** Вид номенклатури → ref.nomenclature_kinds (alembic 0249). Тип
   * (is_service) визначається з виду. */
  kind_id: string | null;
  /** Рахунки обліку (override проводок) + податкові призначення (alembic 0261). */
  account_id: string | null;
  transfer_account_id: string | null;
  off_balance_account_id: string | null;
  off_balance_transfer_account_id: string | null;
  tax_purpose_id: string | null;
  income_cost_tax_purpose_id: string | null;
  deletion_mark: boolean;
  // Значок «І» імпорту з 1С/BAS.
  external_source?: string | null;
  created_at: string;
  updated_at: string;
}

// UOM classifier (2026-06-02).
export interface UomClassifier {
  id: string;
  code: string;
  name: string;
  short_name: string;
  international_code: string;
}

export type NomenclatureCreate = Pick<
  Nomenclature,
  'code' | 'article' | 'name' | 'full_name' | 'is_service' | 'is_excise' | 'ukt_zed_code' | 'country_origin_code' | 'base_uom_id' | 'vat_rate_id' | 'default_cost_item_id' | 'kind_id'
  | 'account_id' | 'transfer_account_id' | 'off_balance_account_id' | 'off_balance_transfer_account_id' | 'tax_purpose_id' | 'income_cost_tax_purpose_id'
> & { use_series?: boolean; barcode?: string | null };

export const nomenclature = {
  list: (params?: { q?: string; limit?: number; offset?: number; kind_id?: string; sort_by?: string; sort_dir?: 'asc' | 'desc' }) =>
    api.get<Page<Nomenclature>>('/nomenclature', { params }).then((r) => r.data),
  /** ч.1360: УВЕСЬ довідник сторінками по 2000 до кінця (ліміт API) — для вікна підбору,
   *  рядкових комбобоксів і мап id → назва. Раніше 58 місць тягнули `limit: 1000` або
   *  `2000` під трьома різними ключами: у акаунті з 1394 позиціями «Юридичні послуги»
   *  (за абеткою після першої тисячі) не було у підборі послуг рахунку / замовлення /
   *  реалізації — і пошуком теж. Один ключ `['nomenclature-full']` = один queryFn (ч.1182). */
  listAll: async (): Promise<Page<Nomenclature>> => {
    const limit = 2000;
    const items: Nomenclature[] = [];
    let offset = 0;
    let total = 0;
    for (;;) {
      const page = await api.get<Page<Nomenclature>>('/nomenclature', { params: { limit, offset } })
        .then((r) => r.data);
      items.push(...page.items);
      total = page.total;
      offset += page.items.length;
      if (page.items.length < limit || offset >= total) break;
    }
    return { items, total: Math.max(total, items.length), limit, offset: 0 } as Page<Nomenclature>;
  },
  /** ч.1059: картка номенклатури — окрема сторінка /nomenclature/:id. */
  get: (id: string) =>
    api.get<Nomenclature>(`/nomenclature/${id}`).then((r) => r.data),
  create: (payload: NomenclatureCreate) =>
    api.post<Nomenclature>('/nomenclature', payload).then((r) => r.data),
  update: (id: string, payload: Partial<NomenclatureCreate>) =>
    api.patch<Nomenclature>(`/nomenclature/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/nomenclature/${id}`).then(() => undefined),
  /** POS lookup за штрих-кодом (Порція Роздріб.6). 404 коли нема. */
  lookupByBarcode: (barcode: string) =>
    api.get<Nomenclature>('/nomenclature/lookup', { params: { barcode } }).then((r) => r.data),
};

// ===== UKTZED codes (Підпорція 3.1a, 2026-05-24) =====

export interface UktzedCodeItem {
  id: string;
  code: string;           // 10 цифр
  name_uk: string;
  parent_code: string | null;
  level: number;
  duty_rate_percent: number | null;
  is_active: boolean;
}

export interface UktzedSearchResponse {
  items: UktzedCodeItem[];
  total: number;
}

export const uktzedCodes = {
  search: (params?: {
    q?: string;
    code_prefix?: string;
    parent_code?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<UktzedSearchResponse>('/refs/uktzed-codes', { params })
      .then((r) => r.data),
};

// ДКПП (ДК 016:2010) — довідник послуг для «Код для ПН».
export interface DkppCodeItem {
  code: string;
  name_uk: string;
  level: number;
}
export interface DkppSearchResponse {
  items: DkppCodeItem[];
  total: number;
}
export const dkppCodes = {
  search: (params?: {
    q?: string;
    code_prefix?: string;
    parent_code?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<DkppSearchResponse>('/refs/dkpp-codes', { params })
      .then((r) => r.data),
};
